import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Chart from "react-apexcharts";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import { useLocationContext } from "context/LocationContext";
import { QUARTER_OPTIONS, YEAR_OPTIONS } from "./monitoringConstants";
import useMonitoringAssessmentScope from "./useMonitoringAssessmentScope";
import {
  createInitialAssessmentSummary,
  getDraftIndicatorStatus,
  getMonitoringSubmissionsForRegionPeriod,
  getStatusProgressValue,
  stripHtml,
} from "./monitoringWorkflow";
import instance from "api/cmsapi";
import useAuth from "hooks/useAuth";
import { toast } from "sonner";
import { useReactToPrint } from 'react-to-print';
import { FilePdfOutlined } from "@ant-design/icons";

const ALL_DISTRICTS_OPTION = { label: "All Districts", value: "all" };

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

const REGIONAL_NOTE_FIELDS = [
  {
    key: "summary",
    label: "Regional Consolidated Summary",
    helper: "Capture the RCC-level synthesis across the district submissions for this monitoring item.",
    placeholder: "Write the consolidated regional summary for this monitoring item...",
  },
  {
    key: "actions",
    label: "Regional Technical Backstopping",
    helper: "Document the follow-up support, technical backstopping, and escalations required.",
    placeholder: "Record the regional technical backstopping and follow-up actions...",
  },
];

const FUNCTIONAL_AREA_COLUMNS = [
  { key: "planningSystem", label: "Planning System", shortLabel: "Planning", maxScore: 25 },
  { key: "procurement", label: "Procurement", shortLabel: "Procurement", maxScore: 4 },
  { key: "environmentalManagement", label: "Environmental Management", shortLabel: "Environment", maxScore: 5 },
  { key: "managementAndOrganization", label: "Management and Organization", shortLabel: "Management", maxScore: 24 },
  { key: "humanResourceManagement", label: "Human Resource Management", shortLabel: "HR", maxScore: 9 },
  {
    key: "financialManagementAndAuditing",
    label: "Financial Management and Auditing",
    shortLabel: "Finance/Audit",
    maxScore: 33,
  },
];

const FUNCTIONAL_AREA_TOTAL_MAX_SCORE = 100;
const DISTRICT_NAME_STOPWORDS = new Set(["metropolitan", "municipal", "district", "assembly"]);

function statusClass(status) {
  if (status === "Complete") return "bg-success/10 text-success";
  if (status === "In Progress") return "bg-[#16325a]/10 text-[#16325a]";
  return "bg-muted text-muted-foreground";
}

function statusIcon(status) {
  if (status === "Complete") return "CheckCircle2";
  if (status === "In Progress") return "ClipboardCheck";
  return "Clock3";
}

function truncateText(value, maxLength = 170) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

function createInitialRegionalNotes(sections) {
  return (Array.isArray(sections) ? sections : []).reduce((acc, section) => {
    acc[section.key] = createInitialAssessmentSummary({ summary: "", actions: "" });
    return acc;
  }, {});
}

function normalizeDistrictLabel(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function getPerformanceRating(totalScore, maxScore) {
  const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
  if (percentage >= 90) return "Excellent";
  if (percentage >= 80) return "Very Good";
  if (percentage >= 70) return "Good";
  if (percentage >= 50) return "Satisfactory";
  return "Poor";
}

function getPerformanceToneClass(totalScore, maxScore) {
  const rating = getPerformanceRating(totalScore, maxScore);
  if (rating === "Excellent") return "bg-success/10 text-success";
  if (rating === "Very Good") return "bg-[#16325a]/10 text-[#16325a]";
  if (rating === "Good") return "bg-warning/15 text-[#9a6700]";
  if (rating === "Satisfactory") return "bg-[#b45309]/10 text-[#b45309]";
  return "bg-destructive/10 text-destructive";
}

function getPerformanceBarColor(totalScore, maxScore) {
  const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
  const rating = getPerformanceRating(totalScore, maxScore);
  if (rating === "Excellent") return "#15803d";
  if (rating === "Very Good") return "#16325a";
  if (rating === "Good") return "#c47f00";
  if (rating === "Satisfactory") return "#b45309";
  return "#b91c1c";
}

function buildDistrictShortCode(name) {
  const parts = String(name || "").split(/[\s-/]+/).map((part) => part.trim()).filter(Boolean);
  const meaningfulParts = parts.filter((part) => !DISTRICT_NAME_STOPWORDS.has(part.toLowerCase()));
  if (meaningfulParts.length === 0) return "MMDA";
  const initials = meaningfulParts.map((part) => part.charAt(0).toUpperCase()).join("");
  if (initials.length >= 2 && initials.length <= 5) return initials;
  if (initials.length > 5) return initials.slice(0, 5);
  if (meaningfulParts.length === 1) return meaningfulParts[0].slice(0, 4).toUpperCase();
  return initials || meaningfulParts[0].slice(0, 4).toUpperCase();
}

function getHeatmapCellClass(score, maxScore) {
  const ratio = maxScore > 0 ? score / maxScore : 0;
  if (ratio >= 0.9) return "bg-success/15 text-success";
  if (ratio >= 0.75) return "bg-[#16325a]/10 text-[#16325a]";
  if (ratio >= 0.5) return "bg-warning/15 text-[#9a6700]";
  return "bg-destructive/10 text-destructive";
}

function RegionRequiredState() {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">Region Selection Required</h2>
        <p className="text-muted-foreground">
          Consolidated Monitoring is available at regional level. Select a region from the sidebar, then open this module.
        </p>
      </div>
    </div>
  );
}

function AssessmentSetupRequiredState({ year, quarter, regionName, onRetry, onChangePeriod }) {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">Assessment Setup Required</h2>
        <p className="text-muted-foreground">
          No assessment has been setup for {year} {quarter} in {regionName || "this region"}.
        </p>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={onRetry}>Try Again</Button>
          <Button variant="outline" onClick={onChangePeriod}>Change Period</Button>
        </div>
      </div>
    </div>
  );
}

function AssessmentPeriodSelection({ selectedYear, selectedQuarter, onYearChange, onQuarterChange, onLoad, isLoading }) {
  return (
    <div className="main-content flex items-center justify-center p-6">
      <div className="w-full max-w-lg border border-slate-200 bg-white rounded-xl shadow-sm">
        <div className="bg-slate-50 border-b border-slate-100 px-8 py-10 text-center rounded-t-xl">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-slate-200 mb-4 shadow-sm">
            <Icon name="Calendar" className="h-6 w-6 text-slate-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">RCC Assessment Period</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-[280px] mx-auto leading-relaxed">
            Select the specific fiscal year and quarter to load the monitoring records.
          </p>
        </div>
        <div className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Assessment Year</label>
              <Select options={YEAR_OPTIONS} value={selectedYear} onChange={onYearChange} placeholder="e.g. 2026" className="w-full" />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Target Quarter</label>
              <Select options={QUARTER_OPTIONS} value={selectedQuarter} onChange={onQuarterChange} placeholder="Select Q" className="w-full" />
            </div>
          </div>
          <Button type="button" variant="default" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white transition-all shadow-md disabled:opacity-20" onClick={onLoad} disabled={!selectedYear || !selectedQuarter || isLoading}>
            {isLoading ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Fetching Assessment...
              </>
            ) : (
              <>
                <Icon name="Search" className="mr-2 h-4 w-4" />
                Load Assessment
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RichTextEditor({ label, helper, placeholder, value, onChange, isEditable = true, sectionName, lastUpdated, lastUpdatedBy }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="flex flex-col xl:grid xl:grid-cols-[220px_minmax(0,1fr)]">
        <div className="border-b border-border bg-muted/25 px-4 py-4 xl:border-b-0 xl:border-r">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{helper}</p>
          {lastUpdated && (
            <p className="mt-2 text-xs text-muted-foreground border-t pt-2">
              Last updated: {lastUpdated} {lastUpdatedBy && `by ${lastUpdatedBy}`}
            </p>
          )}
        </div>
        <div className="p-4">
          <ReactQuill
            theme="snow"
            value={value || ""}
            onChange={onChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder={placeholder}
            readOnly={!isEditable}
          />
        </div>
      </div>
    </div>
  );
}

// API Functions
const getRCCAssessmentByRegion = async (params) => {
  const { regionCode, year, quarter } = params;
  const { data } = await instance.get(
    `rcc-assessments/region/${regionCode}/${year}/${quarter}`
  );
  return data;
};

const getAssessmentSummaryByRegion = async ({ regionCode, year, quarter }) => {
  const { data } = await instance.get(
    `rcc-assessment-summaries/region/${regionCode}/${year}/${quarter}`
  );
  return data;
};

const getRccSubIndicators = async () => {
  const { data } = await instance.get("rcc-sub-indicators");
  return data;
};

// Write-up API Functions
const saveWriteUp = async (payload) => {
  const { data } = await instance.post("write-ups", payload);
  return data;
};

const getWriteUps = async () => {
  try {
    const { data } = await instance.get("write-ups");
    return data;
  } catch (err) {
    console.error("Error fetching write-ups:", err);
    return null;
  }
};

function toArrayPayload(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function getWriteUpTimestamp(writeUp) {
  const updatedAt = Array.isArray(writeUp?.updatedAt) ? writeUp.updatedAt : null;
  const createdAt = Array.isArray(writeUp?.createdAt) ? writeUp.createdAt : null;
  const source = updatedAt || createdAt;
  if (!source || source.length < 3) return 0;
  const [year, month, day, hour = 0, minute = 0, second = 0] = source;
  return new Date(year, month - 1, day, hour, minute, second).getTime();
}

export default function ConsolidatedMonitoringModuleAdapter() {
  const { currentLocation } = useLocationContext();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const defaultYear = YEAR_OPTIONS.find((option) => option.value === String(currentYear)) || YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  // Add ref for PDF generation
  const contentToPrint = useRef(null);

  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0]);
  const [selectedDistrictFocus, setSelectedDistrictFocus] = useState(ALL_DISTRICTS_OPTION);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showAllItems, setShowAllItems] = useState(false);
  const [regionalNotes, setRegionalNotes] = useState({});

  // Executive Summary and Conclusion states
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [savingExecutiveSummary, setSavingExecutiveSummary] = useState(false);
  const [savingConclusion, setSavingConclusion] = useState(false);
  const [existingExecutiveWriteUp, setExistingExecutiveWriteUp] = useState(null);
  const [existingConclusionWriteUp, setExistingConclusionWriteUp] = useState(null);
  const [loadingWriteUps, setLoadingWriteUps] = useState(false);

  const [regionAssessments, setRegionAssessments] = useState([]);
  const [assessmentSummaries, setAssessmentSummaries] = useState([]);
  const [rccSubIndicators, setRccSubIndicators] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState(null);

  const regionCode = useMemo(() => {
    if (!currentLocation?.orgUnitId) return null;
    return `${currentLocation.orgUnitId}`;
  }, [currentLocation]);

  // Section names for write-ups (used as sectionCommented)
  const executiveSummarySection = "ExecutiveSummary";
  const conclusionSection = "Conclusion";

  const regionDistricts = useMemo(() => {
    if (!currentLocation?.children || currentLocation.level !== "region") return [];

    return currentLocation.children
      .filter((child) => child?.level === "district")
      .map((district) => ({
        id: district?.orgUnitId || district?.id || district?.slug || district?.name,
        name: district?.name,
      }))
      .filter((district) => district.id && district.name);
  }, [currentLocation]);

  const districtFocusOptions = useMemo(() => {
    if (regionDistricts.length === 0) return [ALL_DISTRICTS_OPTION];
    return [ALL_DISTRICTS_OPTION, ...regionDistricts.map((district) => ({ label: district.name, value: district.id }))];
  }, [regionDistricts]);

  useEffect(() => {
    if (selectedDistrictFocus?.value !== ALL_DISTRICTS_OPTION.value &&
      !districtFocusOptions.some((option) => option.value === selectedDistrictFocus?.value)) {
      setSelectedDistrictFocus(ALL_DISTRICTS_OPTION);
    }
  }, [districtFocusOptions, selectedDistrictFocus]);

  // Fetch all write-ups and set data
  const fetchAllWriteUps = useCallback(async () => {
    if (!regionCode || !selectedYear?.value) return;

    setLoadingWriteUps(true);
    try {
      const response = await getWriteUps();
      const writeUpRows = toArrayPayload(response);
      const scopedWriteUps = writeUpRows
        .filter(
          (writeUp) =>
            String(writeUp?.locationCode || "") === String(regionCode) &&
            String(writeUp?.year || "") === String(selectedYear.value) &&
            String(writeUp?.type || "Asses") === "Asses"
        )
        .sort((a, b) => getWriteUpTimestamp(b) - getWriteUpTimestamp(a));

      const execWriteUp = scopedWriteUps.find((writeUp) => writeUp.sectionCommented === executiveSummarySection) || null;
      const conclWriteUp = scopedWriteUps.find((writeUp) => writeUp.sectionCommented === conclusionSection) || null;

      setExistingExecutiveWriteUp(execWriteUp);
      setExecutiveSummary(execWriteUp?.comments || "");
      setExistingConclusionWriteUp(conclWriteUp);
      setConclusion(conclWriteUp?.comments || "");
    } catch (err) {
      console.error("Error fetching write-ups:", err);
    } finally {
      setLoadingWriteUps(false);
    }
  }, [regionCode, selectedYear]);

  useEffect(() => {
    if (hasSelectedPeriod && regionCode && selectedYear?.value) {
      fetchAllWriteUps();
    }
  }, [hasSelectedPeriod, regionCode, selectedYear, fetchAllWriteUps]);

  const fetchAllData = useCallback(async () => {
    if (!hasSelectedPeriod || !regionCode) return;

    setIsLoadingData(true);
    setError(null);

    try {
      const year = selectedYear.value;
      const quarter = selectedQuarter.value;

      const assessmentsResponse = await getRCCAssessmentByRegion({ regionCode, year, quarter });
      const assessmentsData = assessmentsResponse?.data || assessmentsResponse || [];
      setRegionAssessments(assessmentsData);

      const summariesResponse = await getAssessmentSummaryByRegion({ regionCode, year, quarter });
      const summariesData = summariesResponse?.data || summariesResponse || [];
      setAssessmentSummaries(summariesData);

      const subIndicatorsResponse = await getRccSubIndicators();
      const subIndicatorsData = toArrayPayload(subIndicatorsResponse);
      setRccSubIndicators(subIndicatorsData);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err);
    } finally {
      setIsLoadingData(false);
    }
  }, [hasSelectedPeriod, regionCode, selectedYear, selectedQuarter]);

  useEffect(() => {
    if (hasSelectedPeriod && regionCode) {
      fetchAllData();
    }
  }, [hasSelectedPeriod, regionCode, fetchAllData]);

  const visibleDistricts = useMemo(() => {
    if (selectedDistrictFocus?.value === ALL_DISTRICTS_OPTION.value) return regionDistricts;
    return regionDistricts.filter((district) => district.id === selectedDistrictFocus?.value);
  }, [regionDistricts, selectedDistrictFocus]);

  // Extract unique indicator names from assessments for dynamic table headers
  const indicatorHeaders = useMemo(() => {
    const indicatorsMap = new Map();

    regionAssessments.forEach((districtData) => {
      districtData.assessments?.forEach((assessment) => {
        if (assessment.assessmentSetup?.indicator) {
          const indicator = assessment.assessmentSetup.indicator;
          if (!indicatorsMap.has(indicator.indicatorId)) {
            indicatorsMap.set(indicator.indicatorId, {
              id: indicator.indicatorId,
              name: indicator.name,
              code: indicator.code,
            });
          }
        }
      });
    });

    return Array.from(indicatorsMap.values());
  }, [regionAssessments]);

  // Calculate total score per indicator for each district (raw score, not percentage)
  const calculateIndicatorTotalScore = useCallback((districtId, indicatorId) => {
    const districtData = regionAssessments.find(d => d.districtCode === districtId);
    if (!districtData?.assessments) return 0;

    let totalScore = 0;

    districtData.assessments.forEach((assessment) => {
      if (assessment.assessmentSetup?.indicator?.indicatorId === indicatorId && assessment.scores) {
        Object.values(assessment.scores).forEach((indicatorScores) => {
          Object.values(indicatorScores).forEach((subScore) => {
            const score = Number(subScore.score) || 0;
            totalScore += score;
          });
        });
      }
    });

    return totalScore;
  }, [regionAssessments]);

  // Calculate max possible score for an indicator across all sub-indicators
  const indicatorMaxScoreByIndicatorId = useMemo(() => {
    const maxScoreMap = new Map();
    rccSubIndicators.forEach((subIndicator) => {
      const indicatorId = subIndicator?.indicator?.indicatorId;
      if (!indicatorId) return;
      const current = maxScoreMap.get(indicatorId) || 0;
      const subIndicatorMax = Number(subIndicator?.maximumScore) || 0;
      maxScoreMap.set(indicatorId, current + subIndicatorMax);
    });
    return maxScoreMap;
  }, [rccSubIndicators]);

  const calculateIndicatorMaxScore = useCallback((indicatorId) => {
    return indicatorMaxScoreByIndicatorId.get(indicatorId) || 0;
  }, [indicatorMaxScoreByIndicatorId]);

  // Build dynamic functional area table based on actual indicators (raw scores)
  const functionalAreaPerformanceRows = useMemo(() => {
    if (visibleDistricts.length === 0 || indicatorHeaders.length === 0) return [];

    const rows = visibleDistricts.map((district) => {
      const row = {
        code: buildDistrictShortCode(district.name),
        districtName: district.name,
        total: 0,
        maxTotal: 0,
      };

      let totalScore = 0;
      let totalMax = 0;

      indicatorHeaders.forEach((indicator) => {
        const score = calculateIndicatorTotalScore(district.id, indicator.id);
        const maxScore = calculateIndicatorMaxScore(indicator.id);
        row[indicator.code] = score;
        row[`${indicator.code}_max`] = maxScore;
        totalScore += score;
        totalMax += maxScore;
      });

      row.total = totalScore;
      row.maxTotal = totalMax;
      row.percentage = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
      row.hasAssessment = regionAssessments.some(d => d.districtCode === district.id);

      return row;
    });

    return rows.sort((a, b) => b.total - a.total || a.districtName.localeCompare(b.districtName));
  }, [visibleDistricts, indicatorHeaders, calculateIndicatorTotalScore, calculateIndicatorMaxScore, regionAssessments]);

  // Calculate averages for each indicator
  const functionalAreaColumnAverages = useMemo(() => {
    if (functionalAreaPerformanceRows.length === 0) return {};

    const averages = {};
    indicatorHeaders.forEach((indicator) => {
      const total = functionalAreaPerformanceRows.reduce((sum, row) => sum + (Number(row[indicator.code]) || 0), 0);
      averages[indicator.code] = (total / functionalAreaPerformanceRows.length).toFixed(1);
    });
    averages.total = (functionalAreaPerformanceRows.reduce((sum, row) => sum + row.total, 0) / functionalAreaPerformanceRows.length).toFixed(1);

    return averages;
  }, [functionalAreaPerformanceRows, indicatorHeaders]);

  // Calculate max scores for header row
  const indicatorMaxScores = useMemo(() => {
    if (functionalAreaPerformanceRows.length === 0) return {};

    const maxScores = {};
    indicatorHeaders.forEach((indicator) => {
      let maxScore = 0;
      functionalAreaPerformanceRows.forEach((row) => {
        const rowMax = row[`${indicator.code}_max`] || 0;
        if (rowMax > maxScore) maxScore = rowMax;
      });
      maxScores[indicator.code] = maxScore;
    });
    maxScores.total = functionalAreaPerformanceRows[0]?.maxTotal || 0;

    return maxScores;
  }, [functionalAreaPerformanceRows, indicatorHeaders]);

  // Build strengths and weaknesses table from assessment summaries
  const strengthWeaknessRows = useMemo(() => {
    const summaryMap = new Map();
    assessmentSummaries.forEach((summary) => {
      summaryMap.set(summary.districtCode, summary);
    });

    return visibleDistricts.map((district) => {
      const summary = summaryMap.get(district.id);
      return {
        districtName: district.name,
        strengths: summary?.strength ? stripHtml(summary.strength) : "No strengths recorded",
        weaknesses: summary?.weakness ? stripHtml(summary.weakness) : "No weaknesses recorded",
        technicalBackstopping: summary?.technicalBackStopping ? stripHtml(summary.technicalBackStopping) : "No backstopping recorded",
        membersAttendance: summary?.membersAttendance || 0,
        aapImplementation: summary?.aapImplementation || 0,
        ledImplementation: summary?.ledImplementation || 0,
        ccDrrImplementation: summary?.ccDrrImplementation || 0,
        managementDecisionsImplementation: summary?.managementDecisionsImplementation || 0,
        capacityBuildingImplementation: summary?.capacityBuildingImplementation || 0,
        budgetCommitteeAttendance: summary?.budgetCommitteeAttendance || 0,
        igfGrowthRate: summary?.igfGrowthRate || 0,
      };
    });
  }, [visibleDistricts, assessmentSummaries]);

  const functionalAreaAverageScore = useMemo(() => {
    if (functionalAreaPerformanceRows.length === 0) return 0;
    const total = functionalAreaPerformanceRows.reduce((sum, row) => sum + row.percentage, 0);
    return Math.round(total / functionalAreaPerformanceRows.length);
  }, [functionalAreaPerformanceRows]);

  const functionalAreaPerformanceChart = useMemo(() => {
    const categories = functionalAreaPerformanceRows.slice(0, 15).map((row) => row.code);
    const totals = functionalAreaPerformanceRows.slice(0, 15).map((row) => row.percentage);
    const colors = totals.map((total) => getPerformanceBarColor(total, 100));

    return {
      series: [{ name: "Overall Score (%)", data: totals }],
      options: {
        chart: { type: "bar", toolbar: { show: false }, animations: { enabled: true } },
        plotOptions: { bar: { horizontal: false, distributed: true, borderRadius: 5, columnWidth: "58%" } },
        colors,
        legend: { show: false },
        grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
        xaxis: { categories, labels: { rotate: -45, trim: true, hideOverlappingLabels: false }, title: { text: "Assemblies" } },
        yaxis: { min: 0, max: 100, title: { text: "Score (%)" } },
        dataLabels: { enabled: true, formatter: (value) => `${value}%`, style: { fontSize: "11px", fontWeight: 600, colors: ["#111827"] }, offsetY: -10 },
      },
    };
  }, [functionalAreaPerformanceRows]);

  const rankedPerformanceRows = useMemo(() => {
    return [...functionalAreaPerformanceRows]
      .sort((a, b) => b.total - a.total || a.districtName.localeCompare(b.districtName))
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }, [functionalAreaPerformanceRows]);

  // Create metric datasets from actual assessment summary values
  const metricDatasets = useMemo(() => {
    const districts = strengthWeaknessRows.map(row => buildDistrictShortCode(row.districtName));

    return {
      membersAttendance: strengthWeaknessRows.map(row => row.membersAttendance),
      aapImplementation: strengthWeaknessRows.map(row => row.aapImplementation),
      ledImplementation: strengthWeaknessRows.map(row => row.ledImplementation),
      ccDrrImplementation: strengthWeaknessRows.map(row => row.ccDrrImplementation),
      managementDecisionsImplementation: strengthWeaknessRows.map(row => row.managementDecisionsImplementation),
      capacityBuildingImplementation: strengthWeaknessRows.map(row => row.capacityBuildingImplementation),
      budgetCommitteeAttendance: strengthWeaknessRows.map(row => row.budgetCommitteeAttendance),
      igfGrowthRate: strengthWeaknessRows.map(row => row.igfGrowthRate),
      districts: districts.slice(0, 10)
    };
  }, [strengthWeaknessRows]);

  const createChartOptions = (title, categories, data, color = "#16325a") => ({
    series: [{ name: title, data: data.slice(0, 10) }],
    options: {
      chart: { type: "bar", toolbar: { show: false }, height: 350 },
      colors: [color],
      plotOptions: { bar: { borderRadius: 5, columnWidth: "55%", horizontal: false } },
      dataLabels: { enabled: false },
      xaxis: { categories: categories.slice(0, 10), labels: { rotate: -45, style: { fontSize: "10px" } } },
      yaxis: { min: 0, max: 100, title: { text: "Percentage (%)" } },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      title: { text: title, align: "center", style: { fontSize: "14px", fontWeight: "bold" } },
    },
  });

  // Format date for display
  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3) return null;
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(year, month - 1, day, hour || 0, minute || 0, second || 0).toLocaleString();
  };

  // Save Executive Summary to write-ups API
  const handleSaveExecutiveSummary = async () => {
    setSavingExecutiveSummary(true);
    const currentUsername = user?.user?.username || "system";
    const currentFullName = user?.user?.fullName || currentUsername;
    const normalizedUserRole = user?.user?.role || "regional_officer";

    const payload = {
      writeUpId: existingExecutiveWriteUp?.writeUpId || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: normalizedUserRole,
      type: "Asses",
      title: "Regional Executive Summary",
      locationCode: regionCode,
      year: selectedYear.value,
      sectionCommented: executiveSummarySection,
      comments: executiveSummary,
      createdBy: currentUsername,
    };

    try {
      const response = await saveWriteUp(payload);
      if (response?.data) {
        setExistingExecutiveWriteUp(response.data);
        toast.success("Executive summary saved successfully");
        fetchAllWriteUps();
      }
    } catch (err) {
      console.error("Error saving executive summary:", err);
      toast.error("Failed to save executive summary");
    } finally {
      setSavingExecutiveSummary(false);
    }
  };

  // Save Conclusion to write-ups API
  const handleSaveConclusion = async () => {
    setSavingConclusion(true);
    const currentUsername = user?.user?.username || "system";
    const currentFullName = user?.user?.fullName || currentUsername;
    const normalizedUserRole = user?.user?.role || "regional_officer";

    const payload = {
      writeUpId: existingConclusionWriteUp?.writeUpId || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: normalizedUserRole,
      type: "Asses",
      title: "Regional Conclusion",
      locationCode: regionCode,
      year: selectedYear.value,
      sectionCommented: conclusionSection,
      comments: conclusion,
      createdBy: currentUsername,
    };

    try {
      const response = await saveWriteUp(payload);
      if (response?.data) {
        setExistingConclusionWriteUp(response.data);
        toast.success("Conclusion saved successfully");
        fetchAllWriteUps();
      }
    } catch (err) {
      console.error("Error saving conclusion:", err);
      toast.error("Failed to save conclusion");
    } finally {
      setSavingConclusion(false);
    }
  };

  const handleYearChange = (option) => {
    setSelectedYear(option);
    setHasSelectedPeriod(false);
    setRegionAssessments([]);
    setAssessmentSummaries([]);
    setExecutiveSummary("");
    setConclusion("");
    setExistingExecutiveWriteUp(null);
    setExistingConclusionWriteUp(null);
  };

  const handleQuarterChange = (option) => {
    setSelectedQuarter(option);
    setHasSelectedPeriod(false);
    setRegionAssessments([]);
    setAssessmentSummaries([]);
    setExecutiveSummary("");
    setConclusion("");
    setExistingExecutiveWriteUp(null);
    setExistingConclusionWriteUp(null);
  };

  const handleLoadAssessment = () => {
    if (selectedYear && selectedQuarter) {
      setHasSelectedPeriod(true);
    }
  };

  // PDF Download handler
  const handlePrint = useReactToPrint({
    content: () => contentToPrint.current,
    documentTitle: `RCC_Consolidated_Monitoring_${currentLocation?.name || "Region"}_${selectedYear?.value || "Year"}_${selectedQuarter?.value || "Quarter"}`,
    onBeforeGetContent: () => {
      // Fix any zero-dimension canvases before printing
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        if (canvas.width === 0 || canvas.height === 0) {
          canvas.width = 800;
          canvas.height = 400;
          canvas.style.width = '800px';
          canvas.style.height = '400px';
        }
      });
      return Promise.resolve();
    },
    onBeforePrint: () => console.log("before printing..."),
    onAfterPrint: () => console.log("after printing..."),
    onPrintError: (error) => {
      console.error("Print error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    },
    removeAfterPrint: true,
  });

  const isLoading = isLoadingData || loadingWriteUps;
  const hasAnyRegionAssessment = regionAssessments.some(
    (districtData) => Array.isArray(districtData?.assessments) && districtData.assessments.length > 0
  );

  if (currentLocation?.level !== "region") return <RegionRequiredState />;

  if (!hasSelectedPeriod) {
    return (
      <AssessmentPeriodSelection
        selectedYear={selectedYear}
        selectedQuarter={selectedQuarter}
        onYearChange={handleYearChange}
        onQuarterChange={handleQuarterChange}
        onLoad={handleLoadAssessment}
        isLoading={isLoading}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="main-content">
        <div className="card flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#16325a] border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading RCC assessment data for {selectedYear?.value} {selectedQuarter?.value}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="card border-red-200 bg-red-50">
          <h2 className="mb-2 text-xl font-semibold text-red-700">Error Loading Data</h2>
          <p className="text-red-600">{error?.message || "An error occurred while loading data"}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setHasSelectedPeriod(false)}>Back to Selection</Button>
            <Button variant="outline" onClick={fetchAllData}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAnyRegionAssessment) {
    return (
      <AssessmentSetupRequiredState
        year={selectedYear?.value}
        quarter={selectedQuarter?.value}
        regionName={currentLocation?.name}
        onRetry={fetchAllData}
        onChangePeriod={() => setHasSelectedPeriod(false)}
      />
    );
  }

  const submittedCount = functionalAreaPerformanceRows.filter(row => row.hasAssessment).length;

  // Get last updated info for display
  const execLastUpdated = existingExecutiveWriteUp?.updatedAt
    ? formatDate(existingExecutiveWriteUp.updatedAt)
    : (existingExecutiveWriteUp?.createdAt ? formatDate(existingExecutiveWriteUp.createdAt) : null);

  const conclLastUpdated = existingConclusionWriteUp?.updatedAt
    ? formatDate(existingConclusionWriteUp.updatedAt)
    : (existingConclusionWriteUp?.createdAt ? formatDate(existingConclusionWriteUp.createdAt) : null);

  return (
    <div className="min-h-full bg-background">
      <div className="main-content space-y-5 pb-6">
        {/* Header Section */}
        <section className="card p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">RCC Consolidated Monitoring</p>
              <h2 className="mt-2 text-2xl font-semibold text-foreground">{currentLocation?.name || "Regional View"}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedYear?.value} {selectedQuarter?.value} Assessment Period
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setHasSelectedPeriod(false)}>
                <Icon name="RefreshCw" className="h-4 w-4" /> Change Period
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                className="bg-[#16325a] hover:bg-[#0f243f]"
                onClick={() => {
                  if (contentToPrint.current) {
                    handlePrint();
                  }
                }}
              >
                <FilePdfOutlined style={{ fontSize: "16px", marginRight: "4px" }} />
                Download PDF
              </Button>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-4">
            <div className="rounded-2xl border border-border bg-background p-3">
              <p className="text-xs text-muted-foreground">Districts in Region</p>
              <p className="text-2xl font-semibold text-foreground">{regionDistricts.length}</p>
            </div>
            <div className="rounded-2xl border border-border bg-background p-3">
              <p className="text-xs text-muted-foreground">Assessments Submitted</p>
              <p className="text-2xl font-semibold text-foreground">{submittedCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-background p-3">
              <p className="text-xs text-muted-foreground">Avg Performance Score</p>
              <p className="text-2xl font-semibold text-foreground">{functionalAreaAverageScore}%</p>
            </div>
            <div className="rounded-2xl border border-border bg-background p-3">
              <p className="text-xs text-muted-foreground">Rating</p>
              <p className="text-2xl font-semibold text-foreground">{getPerformanceRating(functionalAreaAverageScore, 100)}</p>
            </div>
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
            <div className="rounded-3xl border border-border bg-background p-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">District Focus</p>
              <div className="mt-3">
                <Select
                  label="Filter by District"
                  options={districtFocusOptions}
                  value={selectedDistrictFocus}
                  onChange={setSelectedDistrictFocus}
                  placeholder="Select district focus"
                  searchable
                />
              </div>
            </div>
            <div className="rounded-3xl border border-border bg-background p-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Assessment Period</p>
              <div className="mt-2 flex gap-2 text-sm">
                <span className="inline-flex rounded-full bg-muted px-3 py-1">{selectedYear?.value}</span>
                <span className="inline-flex rounded-full bg-[#16325a]/10 px-3 py-1 text-[#16325a]">{selectedQuarter?.value}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Wrap all content to be printed in this div */}
        <div ref={contentToPrint}>
          {/* Executive Summary Section - Before Table 2 */}
          <section className="card overflow-hidden p-0">
            <div className="border-b border-border px-5 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Executive Summary</p>
                <h4 className="text-lg font-semibold text-foreground">Regional Executive Summary</h4>
                <p className="text-sm text-muted-foreground">
                  Provide an executive summary of the regional performance, key findings, and strategic observations.
                </p>
                <p className="text-xs text-muted-foreground font-mono">
                  Location: {regionCode} | Section: {executiveSummarySection} | Year: {selectedYear?.value}
                </p>
              </div>
            </div>
            <div className="p-5">
              <RichTextEditor
                label="Executive Summary"
                helper="Summarize the overall regional performance, key achievements, and critical issues identified during the assessment period."
                placeholder="Write the executive summary for this regional assessment..."
                value={executiveSummary}
                onChange={setExecutiveSummary}
                isEditable={true}
                sectionName={executiveSummarySection}
                lastUpdated={execLastUpdated}
                lastUpdatedBy={existingExecutiveWriteUp?.createdBy}
              />
            </div>
            <div className="border-t border-border px-5 py-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="default"
                  className="bg-[#16325a] hover:bg-[#0f243f]"
                  onClick={handleSaveExecutiveSummary}
                  disabled={savingExecutiveSummary}
                >
                  <Icon name="Save" className="h-4 w-4" />
                  {savingExecutiveSummary ? "Saving..." : "Save Executive Summary"}
                </Button>
              </div>
            </div>
          </section>

          {/* Table 2: Dynamic Functional Areas Performance with RAW SCORES */}
          <section className="card p-0 overflow-hidden">
            <div className="border-b border-border px-5 py-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Table 2</p>
              <h3 className="text-base font-semibold text-foreground">Functional Areas Performance</h3>
              <p className="text-sm text-muted-foreground">Individual district scores across all functional areas (Raw accumulated scores)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 border-r border-border">Rank</th>
                    <th className="px-4 py-3 border-r border-border">Assembly</th>
                    {indicatorHeaders.map((indicator) => (
                      <th key={indicator.id} className="px-4 py-3 border-r border-border">
                        <div className="min-w-[150px]">{indicator.name}</div>
                        <div className="text-[10px] font-normal">(Max: {indicatorMaxScores[indicator.code] || 0})</div>
                      </th>
                    ))}
                    <th className="px-4 py-3 border-r border-border">
                      <div>Total Score</div>
                      <div className="text-[10px] font-normal">(Max: {indicatorMaxScores.total || 0})</div>
                    </th>
                    <th className="px-4 py-3">Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background">
                  <tr className="bg-muted/20 border-b border-border">
                    <td className="px-4 py-3 border-r border-border">-</td>
                    <td className="px-4 py-3 font-semibold border-r border-border">Max Score</td>
                    {indicatorHeaders.map((indicator) => (
                      <td key={`max-${indicator.id}`} className="px-4 py-3 font-medium border-r border-border">
                        {indicatorMaxScores[indicator.code] || 0}
                      </td>
                    ))}
                    <td className="px-4 py-3 font-semibold border-r border-border">{indicatorMaxScores.total || 0}</td>
                    <td className="px-4 py-3">-</td>
                  </tr>
                  {rankedPerformanceRows.map((row) => (
                    <tr key={row.code} className="border-b border-border">
                      <td className="px-4 py-3 border-r border-border">#{row.rank}</td>
                      <td className="px-4 py-3 border-r border-border">
                        <div className="font-medium text-foreground">{row.code}</div>
                        <div className="text-xs text-muted-foreground">{row.districtName}</div>
                      </td>
                      {indicatorHeaders.map((indicator) => (
                        <td key={`${row.code}-${indicator.id}`} className="px-4 py-3 border-r border-border">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getHeatmapCellClass(row[indicator.code] || 0, indicatorMaxScores[indicator.code] || 1)}`}>
                            {row[indicator.code] || 0}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3 font-semibold border-r border-border">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getHeatmapCellClass(row.total, row.maxTotal)}`}>
                          {row.total} / {row.maxTotal}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getPerformanceToneClass(row.total, row.maxTotal)}`}>
                          {getPerformanceRating(row.total, row.maxTotal)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-border bg-muted/15 font-semibold">
                    <td className="px-4 py-3 border-r border-border">-</td>
                    <td className="px-4 py-3 border-r border-border">Average</td>
                    {indicatorHeaders.map((indicator) => (
                      <td key={`avg-${indicator.id}`} className="px-4 py-3 border-r border-border">
                        {functionalAreaColumnAverages[indicator.code] || 0}
                      </td>
                    ))}
                    <td className="px-4 py-3 border-r border-border">{functionalAreaColumnAverages.total || 0}</td>
                    <td className="px-4 py-3">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Table 3: Summary of Strengths and Weaknesses */}
          <section className="card p-0 overflow-hidden">
            <div className="border-b border-border px-5 py-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Table 3</p>
              <h3 className="text-base font-semibold text-foreground">Summary of Strengths and Weaknesses</h3>
              <p className="text-sm text-muted-foreground">Consolidated strengths and weaknesses per assembly</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 border-r border-border">Assembly</th>
                    <th className="px-4 py-3 border-r border-border">Strengths</th>
                    <th className="px-4 py-3">Weaknesses</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background">
                  {strengthWeaknessRows.map((row) => (
                    <tr key={row.districtName} className="align-top border-b border-border">
                      <td className="px-4 py-3 font-medium text-foreground border-r border-border">{row.districtName}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-pre-wrap border-r border-border">{row.strengths}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-pre-wrap">{row.weaknesses}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Performance Chart */}
          {functionalAreaPerformanceRows.length > 0 && (
            <section className="card p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Figure 1</p>
                  <h3 className="text-base font-semibold text-foreground">Overall Performance Scores by Assembly</h3>
                </div>
                <span className="inline-flex rounded-full border border-border bg-background px-3 py-1 text-xs font-medium">
                  Showing {Math.min(15, functionalAreaPerformanceRows.length)} assemblies
                </span>
              </div>
              <div className="mt-4">
                <Chart type="bar" height={400} series={functionalAreaPerformanceChart.series} options={functionalAreaPerformanceChart.options} />
              </div>
            </section>
          )}

          {/* Figures 2-9: Charts Grid */}
          <section className="card p-4 md:p-5">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-foreground">Performance Metrics Dashboard</h3>
              <p className="text-sm text-muted-foreground">Key performance indicators across all assemblies</p>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "Members Attendance", data: metricDatasets.membersAttendance.slice(0, 10) }]} options={createChartOptions("Figure 2: Members Attendance (%)", metricDatasets.districts, metricDatasets.membersAttendance, "#15803d").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "AAP Implementation", data: metricDatasets.aapImplementation.slice(0, 10) }]} options={createChartOptions("Figure 3: AAP Implementation (%)", metricDatasets.districts, metricDatasets.aapImplementation, "#16325a").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "LED Implementation", data: metricDatasets.ledImplementation.slice(0, 10) }]} options={createChartOptions("Figure 4: LED Implementation (%)", metricDatasets.districts, metricDatasets.ledImplementation, "#c47f00").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "CC-DRR Activities", data: metricDatasets.ccDrrImplementation.slice(0, 10) }]} options={createChartOptions("Figure 5: CC-DRR Implementation (%)", metricDatasets.districts, metricDatasets.ccDrrImplementation, "#b45309").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "Management Decisions", data: metricDatasets.managementDecisionsImplementation.slice(0, 10) }]} options={createChartOptions("Figure 6: Management Decisions Implementation (%)", metricDatasets.districts, metricDatasets.managementDecisionsImplementation, "#7c3aed").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "Capacity Building", data: metricDatasets.capacityBuildingImplementation.slice(0, 10) }]} options={createChartOptions("Figure 7: Capacity Building Implementation (%)", metricDatasets.districts, metricDatasets.capacityBuildingImplementation, "#0891b2").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "Budget Committee", data: metricDatasets.budgetCommitteeAttendance.slice(0, 10) }]} options={createChartOptions("Figure 8: Budget Committee Attendance (%)", metricDatasets.districts, metricDatasets.budgetCommitteeAttendance, "#65a30d").options} />
              </div>
              <div className="rounded-xl border border-border p-3">
                <Chart type="bar" height={300} series={[{ name: "IGF Growth", data: metricDatasets.igfGrowthRate.slice(0, 10) }]} options={createChartOptions("Figure 9: IGF Growth Rate (%)", metricDatasets.districts, metricDatasets.igfGrowthRate, "#db2777").options} />
              </div>
            </div>
          </section>

          {/* Table: TECHNICAL BACKSTOPPING PROVIDED */}
          <section className="card p-0 overflow-hidden">
            <div className="border-b border-border px-5 py-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Technical Support Log</p>
              <h3 className="text-base font-semibold text-foreground">TECHNICAL BACKSTOPPING PROVIDED</h3>
              <p className="text-sm text-muted-foreground">Technical backstopping provided to each assembly during the assessment period</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 border-r border-border">Assembly</th>
                    <th className="px-4 py-3">TECHNICAL BACKSTOPPING PROVIDED</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background">
                  {strengthWeaknessRows.map((row) => (
                    <tr key={`backstop-${row.districtName}`} className="align-top border-b border-border">
                      <td className="px-4 py-3 font-medium text-foreground border-r border-border">{row.districtName}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-pre-wrap">{row.technicalBackstopping}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Conclusion Section */}
          <section className="card overflow-hidden p-0">
            <div className="border-b border-border px-5 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Final Section</p>
                <h4 className="text-lg font-semibold text-foreground">Conclusion</h4>
                <p className="text-sm text-muted-foreground">
                  Provide the overall conclusion and key recommendations from this consolidated monitoring exercise.
                </p>
                <p className="text-xs text-muted-foreground font-mono">
                  Location: {regionCode} | Section: {conclusionSection} | Year: {selectedYear?.value}
                </p>
              </div>
            </div>
            <div className="p-5">
              <RichTextEditor
                label="Conclusion"
                helper="Summarize the key takeaways, recommendations, and next steps for the region based on the assessment findings."
                placeholder="Write the conclusion for this regional assessment..."
                value={conclusion}
                onChange={setConclusion}
                isEditable={true}
                sectionName={conclusionSection}
                lastUpdated={conclLastUpdated}
                lastUpdatedBy={existingConclusionWriteUp?.createdBy}
              />
            </div>
            <div className="border-t border-border px-5 py-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="default"
                  className="bg-[#16325a] hover:bg-[#0f243f]"
                  onClick={handleSaveConclusion}
                  disabled={savingConclusion}
                >
                  <Icon name="Save" className="h-4 w-4" />
                  {savingConclusion ? "Saving..." : "Save Conclusion"}
                </Button>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <Icon name="Database" className="inline h-3 w-3 mr-1" />
          Data reflects RCC assessments for {selectedYear?.value} {selectedQuarter?.value}
        </div>
      </div>
    </div>
  );
}